/**
 * Shared constants for document modules
 * Reduces code duplication across all document components
 */

// Status colors and labels for all document types
export const STATUS_COLORS = {
  '0': { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0", label: "Issued" },
  '1': { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Sent" },
  '2': { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb", label: "Draft" },
};

// Status options for filter dropdowns
export const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: '0', label: 'Issued' },
  { value: '1', label: 'Sent' },
  { value: '2', label: 'Draft' },
];

// VAT type colors for invoice modules
export const VAT_TYPE_COLORS = {
  NORMAL_VAT: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  TOMS: { bg: "#fdf4ff", color: "#7c3aed", border: "#e9d5ff" },
};

// Transfer type colors for transfer vouchers
export const TRANSFER_TYPE_COLORS = {
  'ONE WAY': { bg: "#fef3c7", color: "#d97706", border: "#fde68a" },
  'Return': { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" },
};

// Transfer type options for filter dropdowns
export const TRANSFER_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'ONE WAY', label: 'One Way' },
  { value: 'Return', label: 'Return' },
];

// Booking status colors for booking confirmations
export const BOOKING_STATUS_COLORS = {
  '0': { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0", label: "Confirmed" },
  '1': { bg: "#fff7ed", color: "#d97706", border: "#fed7aa", label: "Pending" },
  '2': { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", label: "Cancelled" },
};

// Booking status options for filter dropdowns
export const BOOKING_STATUS_OPTIONS = [
  { value: '', label: 'All Booking Statuses' },
  { value: '0', label: 'Confirmed' },
  { value: '1', label: 'Pending' },
  { value: '2', label: 'Cancelled' },
];

// Insurance type colors for insurance confirmations
export const INSURANCE_TYPE_COLORS = {
  'travel_cancellation': { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Travel & Cancellation" },
  'cancellation':         { bg: "#fdf4ff", color: "#7c3aed", border: "#e9d5ff", label: "Cancellation" },
  'medical':              { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0", label: "Medical" },
};

// Insurance type options for filter dropdowns
export const INSURANCE_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'travel_cancellation', label: 'Travel & Cancellation' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'medical', label: 'Medical' },
];

// SSR direction colors for SSR confirmations
export const SSR_DIRECTION_COLORS = {
  'Outbound': { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Outbound" },
  'Inbound': { bg: "#fdf4ff", color: "#7c3aed", border: "#e9d5ff", label: "Inbound" },
  'Both': { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0", label: "Both" },
};

// SSR direction options for filter dropdowns
export const SSR_DIRECTION_OPTIONS = [
  { value: '', label: 'All Directions' },
  { value: 'Outbound', label: 'Outbound' },
  { value: 'Inbound', label: 'Inbound' },
  { value: 'Both', label: 'Both' },
];

// SSR status colors for SSR confirmations + booking SSR status badges.
// Requested/Confirmed/Cancelled/Denied are the canonical BookingSSR.status ENUM
// values; 'Pending' is kept only for legacy SSR-confirmation documents.
export const SSR_STATUS_COLORS = {
  'Requested': { bg: "#fff7ed", color: "#d97706", border: "#fed7aa", label: "Requested" },
  'Confirmed': { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0", label: "Confirmed" },
  'Pending': { bg: "#fff7ed", color: "#d97706", border: "#fed7aa", label: "Pending" },
  'Cancelled': { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", label: "Cancelled" },
  'Denied': { bg: "#fff1f2", color: "#be123c", border: "#fecdd3", label: "Denied" },
};

// SSR status options for filter dropdowns
export const SSR_STATUS_OPTIONS = [
  { value: '', label: 'All SSR Statuses' },
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Cancelled', label: 'Cancelled' },
];

// Canonical SSR status values — MUST match the BookingSSR.status DB ENUM exactly
// (enum('Requested','Confirmed','Cancelled','Denied')). Used by the editable
// status dropdowns in Add-Booking (Step 5) and Booking Detail.
export const SSR_STATUS_VALUES = ['Requested', 'Confirmed', 'Cancelled', 'Denied'];

// Resolve a status to its badge colors with a safe gray fallback for any
// unexpected/legacy value, so the UI never renders an unstyled or broken pill.
export const ssrStatusColor = (status) =>
  SSR_STATUS_COLORS[status] || { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", label: status || "—" };

// Currency formatter for Belgian Euro
export const formatEur = (amount) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" }).format(amount);

// Document type icons mapping
export const DOCUMENT_ICONS = {
  flight_voucher: 'Plane',
  hotel_voucher: 'Hotel',
  transfer_voucher: 'Car',
  car_rental_voucher: 'KeyRound',
  visa_voucher: 'BookUser',
  cruise_voucher: 'Ship',
  sales_invoice: 'FileText',
  credit_invoice: 'FileX',
  debit_notes: 'FileMinus',
};

// Default pagination settings
export const DEFAULT_PAGINATION = {
  current_page: 1,
  per_page: 10,
  total: 0,
  last_page: 1,
};

// Loading states
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};
